import {
    blobsToVoiceFile,
    buildRecordingFile,
    createAudioRecorder,
    discardMediaRecorder,
    stopMediaRecorderQuietly,
    stopMediaTracks,
} from './audioRecorder';
import { AudioPreviewController } from './audioPreviewController';

export type AudioRecordingSnapshot = {
    isRecording: boolean;
    isPaused: boolean;
    isPreviewPlaying: boolean;
    canResumePreview: boolean;
    previewProgress: number;
    recordingTime: number;
};

export type AudioRecordingSessionOptions = {
    onReady: (file: File) => void;
    onError: (message: string) => void;
    onChange: (snapshot: AudioRecordingSnapshot) => void;
};

const EMPTY: AudioRecordingSnapshot = {
    isRecording: false,
    isPaused: false,
    isPreviewPlaying: false,
    canResumePreview: false,
    previewProgress: 0,
    recordingTime: 0,
};

/**
 * iOS/PWA descarta o buffer final do encoder se stop() é imediato.
 * Esses ms extras entram no fim do arquivo e evitam cortar a última sílaba.
 */
const ENCODER_FLUSH_MS = 250;

type RecorderMode = 'idle' | 'record' | 'pause' | 'flush';

/** Espera o último dataavailable — no Safari ele chega depois do evento stop. */
const STOP_SETTLE_MS = 100;

/**
 * Orquestra MediaRecorder + timer; preview fica em {@link AudioPreviewController}.
 * Cada pausa encerra o recorder (container válido). O envio junta os trechos em WAV.
 */
export class AudioRecordingSession {
    private opts: AudioRecordingSessionOptions;
    private snap: AudioRecordingSnapshot = { ...EMPTY };
    private alive = true;
    private mode: RecorderMode = 'idle';

    private recorder: MediaRecorder | null = null;
    private chunks: Blob[] = [];
    private segments: Blob[] = [];
    private mime = { type: 'audio/mp4', ext: 'm4a' };
    private seal = 0;
    private closing = false;
    private resumePending = false;
    private emitting = false;

    private tick: ReturnType<typeof setInterval> | null = null;
    private flushTimer: ReturnType<typeof setTimeout> | null = null;
    private elapsed = 0;

    private readonly preview = new AudioPreviewController({
        isAlive: () => this.alive,
        getElapsed: () => this.elapsed,
        onPatch: (partial) => this.patch(partial),
        onError: (message) => this.opts.onError(message),
    });

    constructor(opts: AudioRecordingSessionOptions) {
        this.opts = opts;
    }

    getSnapshot(): AudioRecordingSnapshot {
        return this.snap;
    }

    async start(): Promise<void> {
        if (this.mode === 'record' || this.mode === 'flush') return;
        this.segments = [];
        this.chunks = [];
        this.elapsed = 0;
        this.mode = 'record';
        this.patch({ previewProgress: 0, recordingTime: 0, isPaused: false });
        try {
            await this.openRecorder();
        } catch (err) {
            console.error('Erro ao acessar microfone:', err);
            this.mode = 'idle';
            this.opts.onError('Não foi possível acessar o microfone. Verifique as permissões.');
        }
    }

    pause(): void {
        const rec = this.recorder;
        if (!rec || rec.state !== 'recording' || this.mode !== 'record') return;
        this.clearTimers();
        this.mode = 'pause';
        this.closing = true;
        this.patch({ isPaused: true });
        stopMediaRecorderQuietly(rec);
    }

    resume(): void {
        if (this.mode !== 'pause') return;
        if (this.recorder || this.closing) {
            this.resumePending = true;
            return;
        }
        this.preview.stop();
        void this.openRecorder().catch((err) => {
            console.error('Erro ao continuar gravação:', err);
            this.mode = 'pause';
            this.opts.onError('Não foi possível continuar a gravação.');
        });
    }

    stop(): void {
        if (this.mode === 'idle' || this.mode === 'flush') return;
        this.clearTimers();
        this.preview.stop();
        const rec = this.recorder;
        const pausedReady = this.mode === 'pause' && !this.closing && (!rec || rec.state === 'inactive');
        this.mode = 'flush';
        if (pausedReady) {
            void this.emitVoice();
            return;
        }
        if (rec && rec.state === 'recording') {
            this.flushTimer = setTimeout(() => {
                this.flushTimer = null;
                if (!this.alive || !this.recorder || this.recorder.state !== 'recording') return;
                this.closing = true;
                stopMediaRecorderQuietly(this.recorder);
            }, ENCODER_FLUSH_MS);
        }
    }

    cancel(): void {
        this.seal += 1;
        this.mode = 'idle';
        this.resumePending = false;
        this.clearTimers();
        this.preview.stop();
        discardMediaRecorder(this.recorder);
        this.recorder = null;
        this.closing = false;
        this.chunks = [];
        this.segments = [];
        this.elapsed = 0;
        this.patch({ isRecording: false, isPaused: false, recordingTime: 0, previewProgress: 0 });
    }

    togglePreview(): void {
        this.preview.toggle();
    }

    seekPreview(ratio: number): void {
        this.preview.seek(ratio);
    }

    dispose(): void {
        this.alive = false;
        this.seal += 1;
        this.mode = 'idle';
        this.clearTimers();
        this.preview.dispose();
        discardMediaRecorder(this.recorder);
        this.recorder = null;
        this.segments = [];
    }

    private async openRecorder(): Promise<void> {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!this.alive || this.mode === 'idle' || this.mode === 'flush') {
            stopMediaTracks(stream);
            return;
        }

        const { recorder: rec, type, ext } = createAudioRecorder(stream);
        this.mime = { type, ext };
        this.recorder = rec;
        this.chunks = [];
        this.closing = false;
        const seal = ++this.seal;

        rec.ondataavailable = (event) => {
            if (event.data.size) this.chunks.push(event.data);
        };
        rec.onstop = () => {
            setTimeout(() => {
                if (seal !== this.seal) return;
                this.finishRecorderStop(stream);
            }, STOP_SETTLE_MS);
        };

        // Sem timeslice: no Safari/PWA, start(ms) gera um container cuja duração
        // cobre só o primeiro fragmento e o WhatsApp corta a nota de voz.
        rec.start();
        this.mode = 'record';
        this.patch({ isRecording: true, isPaused: false });
        this.startTimer();
    }

    private finishRecorderStop(stream: MediaStream): void {
        this.closing = false;
        stopMediaTracks(stream);

        const piece = new Blob(this.chunks, { type: this.mime.type });
        this.chunks = [];
        this.recorder = null;
        if (piece.size) this.segments.push(piece);
        if (!this.alive) return;

        if (this.mode === 'flush') {
            void this.emitVoice();
            return;
        }
        if (this.mode !== 'pause') return;

        if (this.resumePending) {
            this.resumePending = false;
            this.preview.stop();
            void this.openRecorder().catch((err) => {
                console.error('Erro ao continuar gravação:', err);
                this.mode = 'pause';
                this.opts.onError('Não foi possível continuar a gravação.');
            });
            return;
        }
        this.previewFromSegments();
    }

    private previewFromSegments(): void {
        const only = this.segments.length === 1 ? this.segments[0] : null;
        if (only) {
            this.preview.prepareFromChunks([only], only.type || this.mime.type);
            return;
        }
        void blobsToVoiceFile(this.segments).then((file) => {
            if (!this.alive || this.mode !== 'pause' || !file) return;
            this.preview.prepareFromChunks([file], file.type);
        });
    }

    private async emitVoice(): Promise<void> {
        if (this.emitting) return;
        this.emitting = true;
        const segments = this.segments.splice(0);
        try {
            const file = await blobsToVoiceFile(segments)
                ?? buildRecordingFile(segments, this.mime.type, this.mime.ext);
            if (file && this.alive) this.opts.onReady(file);
        } finally {
            this.emitting = false;
            this.mode = 'idle';
            this.patch({ isRecording: false, isPaused: false });
        }
    }

    private patch(partial: Partial<AudioRecordingSnapshot>): void {
        if (!this.alive) return;
        this.snap = { ...this.snap, ...partial };
        this.opts.onChange(this.snap);
    }

    private clearTimers(): void {
        if (this.tick) {
            clearInterval(this.tick);
            this.tick = null;
        }
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
    }

    private startTimer(): void {
        this.clearTimers();
        this.tick = setInterval(() => {
            if (!this.alive) return;
            this.elapsed += 1;
            this.patch({ recordingTime: this.elapsed });
        }, 1000);
    }
}
